import { invalidateMinicart } from "~/app/store/bffApi";
import { naiveTrackCartContents } from "~/app/analytics/ga/abandonment";
import { occFetch } from "~/app/api/occ/occFetch";
import { assertCurrentCartContext, type CartContext } from "~/features/cart/cart/api/CartEnsure";
import { registerCallProcessor, scheduleCall } from "~/features/cart/cart/api/CartService";
import { cartRequestUrl } from "~/features/cart/cart/api/CartRequestContext";
import { getCurrentBrowserTimezone } from "~/common/utils/dateUtils";
import type { PriceDataDTO } from "~/domains/pricing/types/PriceDataDTO";
import type { MoxyDateString } from "~/common/types/MoxyDate";

export interface AddToCartResponseItem {
  pk: number;
  sapMaterialNumber: string;
  designation: string;
  productCode: string;
  packCode: string;
  packSize: number;
  quantity: number;
  netPrice?: PriceDataDTO;
  actualNetPrice?: PriceDataDTO;
  actualSalePrice?: PriceDataDTO;
  totalPrice?: PriceDataDTO;
  priceUnit?: number;
  priceReference?: string;
  source?: string;
  contractNumber?: string;
  contractLine?: string;
  salesCampaignCode?: string;
  specialPrice?: boolean;
}

export interface AddToCartResultListDTO {
  items?: AddToCartResponseItem[];
}

export interface BulkAddToCartResponseDTO extends AddToCartResultListDTO {
  quantityAdjusted: boolean;
}

export interface AddToCartPartApiItem {
  productCode: string;
  quantity: number;
  packageCode: string;
  packageSize: number;
  primaryWarehouseCode: string;
  partTypeCode: string;
  manualUpdated: boolean;
  originCountry: string;
}

export interface AddToCartApiItem {
  productCode: string;
  packageSize?: number;
  packageCode: string;
  quantity: number;
  createNewPackageEntry?: boolean;

  /**
   * TODO: Kick this out of ADD TO CART API, it's revelant only for quote stuff.
   */
  confirmedQuantity?: number;
  comment?: string;
  selectedDeliveryDate?: MoxyDateString | null;
  selectedDeliveryDateIsManual: boolean;
  // estimatedDeliveryDate?: string;
  primaryWarehouseCode?: string;
  contractNumber?: string;
  contractLine?: string;
  contractOpenQuantity?: number;
  priceReference?: string;

  // Quote specific fields
  quoteLine?: string;
  quoteNumber?: string;
  quoteScheduleLineNum?: string;
  quoteMinQuantity?: number;
  isBundled?: boolean;

  quoteExpiryDate?: MoxyDateString | null;
  logisticalComment?: string;
  estimatedShippingDate?: MoxyDateString | null;

  isRestrictedPackage: boolean;
  orderEntryNumber?: number;

  optionalParts?: AddToCartPartApiItem[];

  customerOrderNumber?: string;
  originCountry: string;

  salesCampaignCode?: string;
}

export function sendAddToCart(
  item: AddToCartApiItem | AddToCartApiItem[],
  skipTracking: boolean,
  cart: CartContext,
): Promise<AddToCartResponseItem[]> {
  return scheduleCall<AddToCartResponseItem[]>("add", {
    cart,
    items: Array.isArray(item) ? item : [item],
    skipTracking,
  });
}

interface AddToCartCall {
  cart: CartContext;
  items: AddToCartApiItem[];
  skipTracking: boolean;
}

registerCallProcessor({
  type: "add",
  canBeExecuted: (ongoingCalls) => {
    const isDeleteOngoing = ongoingCalls.some((call) => call.type === "delete" || call.type === "deleteCart");
    return !isDeleteOngoing;
  },
  execute: async ({ cart, items, skipTracking }: AddToCartCall) => {
    assertCurrentCartContext(cart);
    const body = {
      items,
      userTimeZone: getCurrentBrowserTimezone(),
    };

    const response = await occFetch(
      resolveAddToCartUrl(cart),
      {
        body: JSON.stringify(body),
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
      },
      { accountContext: "omit" },
    );

    if (!response.ok) {
      throw new Error("Failed to add items to cart");
    }

    invalidateMinicart();

    if (!skipTracking) {
      naiveTrackCartContents();
    }

    const data = (await response.json()) as BulkAddToCartResponseDTO;
    return normalizeAddToCartResponseItems(data);
  },
});

export function resolveAddToCartUrl(cart: CartContext): string {
  return cartRequestUrl(cart.requestContext, `/${encodeURIComponent(cart.resourceId)}/entries/bulk`);
}

export function normalizeAddToCartResponseItems(data: AddToCartResultListDTO): AddToCartResponseItem[] {
  return data.items ?? [];
}

export { CartCreationApiError, createCartForAdd, isCartAlreadyExistsForContext } from "./CartCreation";
